import { useEffect, useState } from "react";
import { formatCount, formatMoney, type InventoryReport, type SessionUser } from "@iltocat/shared";
import { ApiError, api } from "../lib/api.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import type { Vocabulary } from "../lib/useVocabulary.js";
import { BuiltReports } from "../components/ReportBuilder.js";
import { Icon, type IconName } from "../components/Icon.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";

/**
 * Stage 6's first piece: what utucat already knows, added up. Nothing here is
 * new data -- `report()` reads the same `price_cents`, `category_id` and
 * `status` columns every other screen already writes -- so this is a read-only
 * screen with no write of its own, the same shape as Export.
 *
 * Amounts are in the inventory's own currency, written by `formatMoney`.
 *
 * Below the summary, the insights and the reports you build
 * (`components/ReportBuilder.tsx`). They replaced the fixed "By category" and
 * "By status" tables and their show/hide chips: grouping what you hold by
 * category or by status is the builder's first report.
 */
export function Reports({
  user,
  taxonomy,
  vocabulary,
  onOpen,
}: {
  user: SessionUser;
  taxonomy: Taxonomy;
  vocabulary: Vocabulary;
  onOpen: (itemId: string) => void;
}) {
  const [report, setReport] = useState<InventoryReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void api
      .report()
      .then(({ report }) => {
        if (live) setReport(report);
      })
      .catch((problem: unknown) => {
        if (live) setError(problem instanceof ApiError ? problem.message : "Could not load the report.");
      });
    return () => {
      live = false;
    };
  }, []);

  const stats: { icon: IconName; label: string; value: string }[] = report
    ? [
        { icon: "chart", label: "Estimated value", value: formatMoney(report.totalValueCents) },
        { icon: "check-circle", label: "Priced items", value: formatCount(report.pricedItems) },
        { icon: "alert", label: "Not priced yet", value: formatCount(report.unpricedItems) },
      ]
    : [];

  return (
    <>
      <PageHeader title="Reports" />
      {error && <Notice tone="error">{error}</Notice>}

      <ul className="stats" aria-busy={report === null}>
        {report === null
          ? Array.from({ length: 3 }, (_, index) => <li key={index} className="stat skeleton" />)
          : stats.map((stat) => (
              <li key={stat.label} className="stat">
                <span className="stat-icon">
                  <Icon name={stat.icon} size={18} />
                </span>
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </li>
            ))}
      </ul>

      <BuiltReports
        kind={user.activeKind}
        canEdit={user.role !== "viewer" && !user.activeArchived}
        taxonomy={taxonomy}
        vocabulary={vocabulary}
        onOpen={onOpen}
      />
    </>
  );
}
